import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { STATUS_STYLE, GUIA_STATUS, fmtDateTime } from './guiasUtils';

export const StatusBadge = ({ status, testId }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pendente;
  return (
    <Badge variant="outline" className={`gap-1.5 font-medium ${s.badge}`} data-testid={testId}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {s.label}
    </Badge>
  );
};

export const GuiaStatusBadge = ({ status }) => {
  const s = GUIA_STATUS[status] || GUIA_STATUS.nao_enviada;
  return <Badge className={`${s.cls} border-0 hover:${s.cls}`} data-testid={`guia-status-${status}`}>{s.label}</Badge>;
};

export const GuiaViewer = ({ guiaId, open, onClose, children }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!open || !guiaId) return;
    setLoading(true);
    setData(null);
    setImgError(false);
    api.get(`/guias/arquivo/${guiaId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [open, guiaId, api]);

  const isPdf = data?.arquivo_tipo === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="guia-viewer-dialog">
        <DialogHeader>
          <DialogTitle>Guia — {data?.paciente_nome || '...'}</DialogTitle>
          {!data && <DialogDescription>Carregando arquivo da guia...</DialogDescription>}
          {data && (
            <DialogDescription asChild>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs pt-1">
                <span><b>Profissional:</b> {data.profissional_nome}</span>
                <span><b>Categoria:</b> {data.categoria_label}</span>
                <span><b>Enviada por:</b> {data.enviado_por_nome}</span>
                <span><b>Data:</b> {fmtDateTime(data.enviado_em)}</span>
                <span><b>Arquivo:</b> {data.arquivo_nome}{data.versao > 1 ? ` (v${data.versao})` : ''}</span>
                <span><b>Status:</b> <GuiaStatusBadge status={data.status} /></span>
              </div>
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="min-h-[300px] flex items-center justify-center bg-muted/40 rounded-lg overflow-hidden">
          {loading && <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />}
          {data && !loading && (isPdf
            ? <iframe title="guia-pdf" src={data.arquivo} className="w-full h-[70vh]" data-testid="guia-pdf-frame" />
            : imgError
              ? <a href={data.arquivo} download={data.arquivo_nome || 'guia'} className="text-sm underline text-primary p-6" data-testid="guia-download-link">Não foi possível exibir a imagem. Clique para baixar o arquivo.</a>
              : <img src={data.arquivo} alt="Guia" onError={() => setImgError(true)} className="max-h-[70vh] min-h-[200px] w-auto object-contain" data-testid="guia-image" />)}
        </div>
        {children}
      </DialogContent>
    </Dialog>
  );
};
